import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Helmet } from 'react-helmet';
import TabNavigation from './components/TabNavigation';
import { TagBadge } from './components/ui/tag-badge.tsx';
import VideoEmbed from './components/VideoEmbed';
import ArticleEmbed from './components/ArticleEmbed';
import TwitterEmbed from './components/TwitterEmbed';

const ListingDetails = () => {
  const { idOrSlug } = useParams();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    const fetchListing = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/listings/${idOrSlug}`);
        if (!response.data) {
          throw new Error('Listing data is empty');
        }
        setListing(response.data);
      } catch (err) {
        setError(err.response?.data?.message || err.message || 'Failed to fetch listing');
        setListing(null);
      } finally {
        setLoading(false);
      }
    };

    fetchListing();
  }, [idOrSlug]);

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;
  if (!listing) return <div>Listing not found</div>;

  const title = listing.properties?.['AI Product Name']?.title[0]?.plain_text || 'Untitled';
  const description = listing.properties?.['Summary (short text)']?.rich_text[0]?.plain_text || 'No description';
  const logo = listing.properties?.['Logo Image']?.url || '/logo192.png';
  const category = listing.properties?.Category?.rich_text[0]?.plain_text || '';
  const founder = listing.properties?.Founder?.rich_text[0]?.plain_text || '';
  const pricingModel = listing.properties?.['Pricing Model']?.rich_text[0]?.plain_text || '';
  const keyFeatures = listing.properties?.['Key Features']?.rich_text[0]?.plain_text || '';
  const apiAvailable = listing.properties?.['API Available']?.checkbox || false;
  const tags = listing.properties?.Tags?.rich_text[0]?.plain_text || '';

  const renderTabContent = () => {
    switch (activeTab) {
      case 'videos':
        const videoLinks = listing.properties?.['YouTube Links']?.rich_text[0]?.plain_text;
        return (
          <div className="space-y-4">
            {videoLinks ? (
              videoLinks.split('\n').map((url, i) => (
                url.trim() && <VideoEmbed key={i} url={url} index={i} />
              ))
            ) : (
              <p className="text-muted">No video links available</p>
            )}
          </div>
        );
      case 'articles':
        const articleLinks = listing.properties?.['Article Links']?.rich_text[0]?.plain_text;
        return (
          <div className="space-y-4">
            {articleLinks ? (
              articleLinks.split('\n').map((url, i) => (
                url.trim() && <ArticleEmbed key={i} url={url} index={i} />
              ))
            ) : (
              <p className="text-muted">No article links available</p>
            )}
          </div>
        );
      case 'discussions':
        const discussionLinks = listing.properties?.['Twitter Links']?.rich_text[0]?.plain_text;
        return (
          <div className="space-y-4">
            {discussionLinks ? (
              discussionLinks.split('\n').map((url, i) => (
                url.trim() && <TwitterEmbed key={i} url={url} index={i} />
              ))
            ) : (
              <p className="text-muted">No discussion links available</p>
            )}
          </div>
        );
      default:
        return (
          <>
            <div className="flex items-start gap-6 mb-8">
              <img 
                src={logo} 
                alt={title} 
                className="w-24 h-24 rounded-logo object-cover shadow-card"
              />
              <div className="flex-1">
                <h1 className="text-3xl font-bold mb-2">{title}</h1>
                <div className="flex items-center gap-4 mb-4">
                  <span className="text-sm text-muted">{category}</span>
                </div>
                <div className="flex flex-wrap gap-2 mb-4">
                  {tags.split(',').map((tag, i) => (
                    <TagBadge key={i} tag={tag.trim()} />
                  ))}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
              <div>
                <h2 className="text-xl font-bold mb-2">Description</h2>
                <p className="text-muted leading-relaxed">{description}</p>
              </div>
              <div>
                <h2 className="text-xl font-bold mb-2">Founder</h2>
                <p className="text-muted">{founder}</p>
              </div>
              <div>
                <h2 className="text-xl font-bold mb-2">Pricing Model</h2>
                <p className="text-muted">{pricingModel}</p>
              </div>
              <div>
                <h2 className="text-xl font-bold mb-2">Key Features</h2>
                <ul className="list-disc list-inside text-muted">
                  {keyFeatures.split('\n').map((feature, i) => (
                    <li key={i}>{feature.trim()}</li>
                  ))}
                </ul>
              </div>
              <div>
                <h2 className="text-xl font-bold mb-2">API Availability</h2>
                <div className="flex items-center gap-2">
                  {apiAvailable ? (
                    <span className="text-green-600 flex items-center gap-1">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      API Available
                    </span>
                  ) : (
                    <span className="text-red-600 flex items-center gap-1">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                      API Not Available
                    </span>
                  )}
                </div>
              </div>
            </div>
          </>
        );
    }
  };

  return (
    <>
      <Helmet>
        <title>{title} | iReview.ai</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={`${title} | iReview.ai`} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={`https://ireview.ai/listings/${idOrSlug}`} />
        <meta property="og:image" content={logo} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${title} | iReview.ai`} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={logo} />
        <script type="application/ld+json">
          {`{
            "@context": "https://schema.org",
            "@type": "Product",
            "name": "${title}",
            "description": "${description}",
            "image": "${logo}",
            "brand": {
              "@type": "Brand",
              "name": "${founder}"
            },
            "offers": {
              "@type": "Offer",
              "price": "${pricingModel}",
              "priceCurrency": "USD"
            }
          }`}
        </script>
      </Helmet>
      <div className="container mx-auto px-4 py-8 flex-1">
      <div className="bg-card rounded-card shadow-card p-8">
        <TabNavigation 
          tabs={['overview', 'videos', 'articles', 'discussions']}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />
        {renderTabContent()}
      </div>
    </div>
    </>
  );
};

export default ListingDetails;
